
import React, { useState } from 'react';
import { RenovationQuote } from '../../types';
import { FileCheck, X, Plus, Save } from 'lucide-react';

interface BudgetEditorProps {
  taskTitle: string;
  existingQuotes: RenovationQuote[];
  onSave: (quotes: RenovationQuote[]) => void;
  onClose: () => void;
}

const BudgetEditor: React.FC<BudgetEditorProps> = ({ taskTitle, existingQuotes, onSave, onClose }) => {
  const [quotes, setQuotes] = useState<RenovationQuote[]>(existingQuotes || []);

  const addQuote = () => {
    const newQuote: RenovationQuote = {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      providerName: 'New Bidder',
      totalAmount: 0,
      includes: ['Labor', 'Materials'],
      estimatedDurationDays: 5,
      isSelected: false
    };
    setQuotes([...quotes, newQuote]);
  };

  const updateQuote = (id: string, field: keyof RenovationQuote, value: any) => {
    setQuotes(quotes.map(q => q.id === id ? { ...q, [field]: value } : q));
  };

  const deleteQuote = (id: string) => {
    setQuotes(quotes.filter(q => q.id !== id));
  };

  const handleSave = () => {
    onSave(quotes);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={onClose}></div>
      <div className="bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 w-full max-w-3xl relative flex flex-col max-h-[85vh] animate-in fade-in zoom-in-95 duration-200">
        <div className="px-6 py-4 border-b border-slate-800 flex justify-between items-center bg-slate-900 z-10 rounded-t-2xl">
          <h3 className="font-bold text-lg text-white flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-indigo-500" /> Bids for: {taskTitle}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-4 custom-scrollbar">
          {quotes.map(quote => (
            <div key={quote.id} className="bg-slate-950 border border-slate-800 rounded-xl p-4 relative group hover:border-indigo-500/50 transition-colors">
              <button 
                onClick={() => deleteQuote(quote.id)} 
                className="absolute top-2 right-2 text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="space-y-3">
                <div>
                    <label className="text-[10px] text-slate-500 font-bold uppercase">Contractor</label>
                    <input 
                        value={quote.providerName}
                        onChange={(e) => updateQuote(quote.id, 'providerName', e.target.value)}
                        className="bg-transparent font-bold text-white w-full outline-none border-b border-slate-800 focus:border-indigo-500 py-1"
                        placeholder="Provider Name"
                    />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[9px] text-slate-500 font-bold uppercase">Price (€)</label>
                    <input 
                      type="number"
                      value={quote.totalAmount}
                      onChange={(e) => updateQuote(quote.id, 'totalAmount', Number(e.target.value))}
                      className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-emerald-400 font-mono font-bold outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-[9px] text-slate-500 font-bold uppercase">Duration (Days)</label>
                    <input 
                      type="number"
                      value={quote.estimatedDurationDays}
                      onChange={(e) => updateQuote(quote.id, 'estimatedDurationDays', Number(e.target.value))}
                      className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-white text-sm outline-none focus:border-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          <button 
            onClick={addQuote}
            className="flex flex-col items-center justify-center border-2 border-dashed border-slate-800 rounded-xl p-4 text-slate-500 hover:border-indigo-500/50 hover:text-indigo-400 hover:bg-slate-900/50 transition-all min-h-[140px]"
          >
            <Plus className="w-8 h-8 mb-2 opacity-50" />
            <span className="text-xs font-bold uppercase">Add New Bid</span>
          </button>
        </div>

        <div className="px-6 py-4 border-t border-slate-800 bg-slate-900 rounded-b-2xl flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white font-medium transition-colors">Cancel</button>
            <button 
                onClick={handleSave}
                className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2 rounded-xl font-bold shadow-lg shadow-indigo-900/20 transition-all flex items-center gap-2"
            >
                <Save className="w-4 h-4" /> Save Bids
            </button>
        </div>
      </div>
    </div>
  );
};

export default BudgetEditor;
